import {
  captureException,
  captureMessage,
  Severity,
  withScope,
} from '@sentry/browser'
import {filter} from 'lodash'

import Browser from '@base/browser'
import getLocation from '@base/browser-locator/getLocation'
import {
  CUSTOM_METADATA_CONTEXT,
  normalizeError,
} from '@base/utils/errors/sentry'
import {completeAssign} from '@base/utils/object'
import {uuid} from '@base/utils/uuid'

import type ProberBackendType from './backends/ProberBackend'
import {ActionEvent, TimingEvent, CustomEvent} from './events'
import type ProberEventType from './events/ProberEvent'
import type ClickMonitor from './monitors/ClickMonitor'

// typecheck: ignore statement
// typecheck: ignore statement

export default class Prober {
  backends: Array<ProberBackendType> = []
  eventsDataLog: Array<any> = []
  visitIndex: number = 0
  tabUid: string = ''
  data: any = {}
  clickMonitor?: ClickMonitor
  capturedExceptions: WeakSet<any> = new WeakSet()

  metadata: {
    visitNr: number
    system: any
    location?: any | null
  }

  constructor() {
    this.initVisitUid()
    this.metadata = {
      visitNr: this.visitIndex,
      system: this.getSystemInfo(),
    }
  }

  initVisitUid() {
    const {tab, visit} = this.parseWindowName()

    this.visitIndex = visit ? parseInt(visit, 10) + 1 : 1
    this.tabUid = tab || uuid()

    window.name = `${this.tabUid}/${this.visitIndex}`
  }

  initMetadata() {
    // typecheck: ignore statement
  }

  async initLocation() {
    try {
      this.setMetadata({location: await getLocation()})
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Could not resolve location for Prober', e)
    }
  }

  addDataPoint(dataPoint: ProberEventType) {
    try {
      if (this.getMemoryInfo()) {
        dataPoint.addData({
          ...this.getMemoryInfo().memory,
        })
      }

      this.eventsDataLog.push(dataPoint.data)

      this.backends.forEach((backend) => {
        backend.handleDataPoint(dataPoint)
      })
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e.stack)
    }

    return this
  }

  // Searches logged events and returns their data (not the Event objects)
  // Used by packages/@base-tooling/ci-audits/src/ProberGatherer.ts
  // For development/debugging purposes, see devTools.Prober
  findAll(match?: any) {
    return filter(this.eventsDataLog, match || Boolean)
  }

  captureCustom(name: string, value: any) {
    const event = new CustomEvent({
      name,
      value,
    })
    this.addDataPoint(event)
  }

  captureAction(
    category: string | ActionEvent,
    action?: string,
    customData?: any
  ) {
    let event
    if (category instanceof ActionEvent) {
      event = category
    } else if (action) {
      event = new ActionEvent(category, action)
      if (customData) {
        event.addData(customData)
      }
    } else {
      throw new Error('action is missing')
    }

    this.addDataPoint(event)
    return event
  }

  trackExceptionCapture(exception: any) {
    if (typeof exception !== 'object') {
      return false
    }
    if (this.capturedExceptions.has(exception)) {
      return true
    }
    this.capturedExceptions.add(exception)
    return false
  }

  captureException(
    category: string,
    exception: string | Error | any,
    customData?: any | null
  ) {
    if (this.trackExceptionCapture(exception)) {
      if (DEVELOPMENT) {
        // eslint-disable-next-line no-console
        console.info(
          `[REPEATED EXCEPTION] ${category}: %o (%o)`,
          exception,
          customData
        )
      }
      return
    }

    const {fatal, ...extra} = customData || {}

    // All warnings & exceptions are reported through Sentry - this way, we have a single source
    // of errors, as Sentry will capture unhandled errors for us.
    // Also, Sentry normalises errors & stack traces, so it makes sense to report errors to Prober AFTER Sentry
    withScope((scope) => {
      scope.setTags({category})
      scope.setLevel(fatal ? Severity.Error : Severity.Warning)
      scope.setContext(CUSTOM_METADATA_CONTEXT, extra)
      if (typeof exception === 'string') {
        captureMessage(exception)
      } else {
        captureException(normalizeError(exception))
      }
    })
  }

  captureTiming(category: string, action: string, time?: number | null) {
    const event = new TimingEvent(category, action, time)
    this.addDataPoint(event)

    return event
  }

  setDefaultEventData(data: any) {
    this.data = completeAssign({}, this.data, data)
  }

  getDefaultEventData(): any {
    return this.data
  }

  setMetadata(data?: any | null) {
    this.metadata = Object.assign({}, this.metadata, data)
  }

  getMetadata(): any {
    this.addMemoryInfo()
    return this.metadata
  }

  registerBackend(backend: ProberBackendType) {
    this.backends.push(backend)
  }

  getWindowUUID() {
    return this.tabUid
  }

  parseWindowName(): {
    tab?: string
    visit?: string
  } {
    const windowNameComponents = String(window.name).match(
      /^(.+?)(?:\/(\d+))?$/
    )
    if (windowNameComponents) {
      return {
        tab: windowNameComponents[1],
        visit: windowNameComponents[2],
      }
    }
    return {
      tab: undefined,
      visit: undefined,
    }
  }

  getVisitIndex(): number {
    return this.visitIndex
  }

  getSystemInfo(): any {
    return {
      // @ts-ignore
      os: Browser.osTitle,
      // @ts-ignore
      browser: `${Browser.title} ${Browser.version}`,
      appVersion: navigator.appVersion,
    }
  }

  getMemoryInfo(): any | undefined | null {
    // @ts-ignore
    if (window.performance && window.performance.memory) {
      return {
        // We need to access the memory props directly, as performance.memory does not stringify
        memory: {
          // @ts-ignore
          jsHeapSizeLimit: window.performance.memory.jsHeapSizeLimit,
          // @ts-ignore
          usedJSHeapSize: window.performance.memory.usedJSHeapSize,
          // @ts-ignore
          totalJSHeapSize: window.performance.memory.totalJSHeapSize,
        },
      }
    }
    return null
  }

  addMemoryInfo() {
    try {
      this.setMetadata(this.getMemoryInfo())
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }

  startTestEnvironment(testName: string) {
    this.setMetadata({
      test: testName,
    })
  }

  stopTestEnvironment() {
    this.setMetadata({
      test: 'done',
    })
  }
}

export type ProberType = Prober
